import { createSlice } from "@reduxjs/toolkit";
import { userProjectApi } from 'Services/UserProjectService'
import { sharedProjectApi } from 'Services/SharedProjectService'

// Fields that track "edited vs. still the original AI content" — the only ones with an `.edited`
// key in their slice state.
const AI_TRACKED_FIELDS = ['targetBeneficiaries', 'tags', 'measurableOutcomes', 'geographicAreasServed', 'similarCharities'];

export const initialState = {
    openFormDialog: false,
    openDeleteDialog: false,
    tabIndex: 0,
    pagedList: {
        pageNumber: 0,
        pageSize: 10,
        pageCount: 0,
        totalCount: 0,
        showArchived: false,
        orderField: 'projectStatus',
        orderDirection: 'asc',
        items: [],
        activeUserProjectId: ''
    },
    pagedSharedList: {
        pageNumber: 0,
        pageSize: 10,
        pageCount: 0,
        totalCount: 0,
        showArchived: false,
        orderField: 'projectStatus',
        orderDirection: 'asc',
        items: [],
        activeUserProjectId: ''
    },
    projectStatusList: [ 'Active', 'Completed', 'InProgress' ],
    id: null,
    projectName: null,
    searchTerm: {
        value: '',
        error: ''
    },
    name: {
        value: '',
        error: ''
    },
    startDate: {
        value: null,
        error: ''
    },
    endDate: {
        value: null,
        error: ''
    },
    projectCost: {
        value: '',
        error: ''
    },
    projectDescription: {
        value: '',
        error: ''
    },
    projectStatus: {
        value: '',
        error: ''
    },
    aiEnabled: false,
    aiGenerated: false,
    targetBeneficiaries: {
        value: [],
        error: '',
        list: [],
        aiSelected: [],
        generating: false,
        justPopulated: false,
        confirmRefresh: false,
        edited: false,
    },
    measurableOutcomes: {
        value: [],
        error: '',
        aiTexts: [],
        list: [],
        generating: false,
        justPopulated: false,
        confirmRefresh: false,
        edited: false,
    },
    tags: {
        value: [],
        error: '',
        list: [],
        aiSelected: [],
        generating: false,
        justPopulated: false,
        confirmRefresh: false,
        edited: false,
    },
    otherFunding: {
        value: [],
        error: ''
    },
    geographicAreasServed: {
        value: [],
        error: '',
        list: [],
        aiSelected: [],
        generating: false,
        justPopulated: false,
        confirmRefresh: false,
        edited: false,
    },
    similarCharities: {
        value: [],
        error: '',
        list: [],
        aiSelected: [],
        generating: false,
        justPopulated: false,
        confirmRefresh: false,
        edited: false,
    },
};

const resetAiState = (state) => {
    state.aiEnabled = false;
    state.aiGenerated = false;

    state.targetBeneficiaries.list = [];
    state.targetBeneficiaries.aiSelected = [];
    state.targetBeneficiaries.generating = false;
    state.targetBeneficiaries.justPopulated = false;
    state.targetBeneficiaries.confirmRefresh = false;
    state.targetBeneficiaries.edited = false;

    state.tags.list = [];
    state.tags.aiSelected = [];
    state.tags.generating = false;
    state.tags.justPopulated = false;
    state.tags.confirmRefresh = false;
    state.tags.edited = false;

    state.measurableOutcomes.aiTexts = [];
    state.measurableOutcomes.list = [];
    state.measurableOutcomes.generating = false;
    state.measurableOutcomes.justPopulated = false;
    state.measurableOutcomes.confirmRefresh = false;
    state.measurableOutcomes.edited = false;

    state.geographicAreasServed.list = [];
    state.geographicAreasServed.aiSelected = [];
    state.geographicAreasServed.generating = false;
    state.geographicAreasServed.justPopulated = false;
    state.geographicAreasServed.confirmRefresh = false;
    state.geographicAreasServed.edited = false;

    state.similarCharities.list = [];
    state.similarCharities.aiSelected = [];
    state.similarCharities.generating = false;
    state.similarCharities.justPopulated = false;
    state.similarCharities.confirmRefresh = false;
    state.similarCharities.edited = false;
};

const userProjectSlice = createSlice({
    name: "user-project",
    initialState,
    reducers: {
        handleCloseDialog: (state, action) => {
            state.openFormDialog = false;
            state.openDeleteDialog = false;
        },
        handleOpenDeleteDialog: (state, action) => {
            state.id = action.payload.id;
            state.openFormDialog = false;
            state.openDeleteDialog = true;
        },
        handleCreateNewProject: (state, action) => {
            state.openFormDialog = true;
            state.id = null;
            state.projectName = null;

            state.name.value = '';
            state.name.error = '';

            state.startDate.value = null;
            state.startDate.error = '';

            state.endDate.value = null;
            state.endDate.error = '';

            state.projectCost.value = '';
            state.projectCost.error = '';

            state.projectDescription.value = '';
            state.projectDescription.error = '';

            state.projectStatus.value = '';
            state.projectStatus.error = '';

            state.targetBeneficiaries.value = []
            state.targetBeneficiaries.error = '';

            state.measurableOutcomes.value = [];
            state.measurableOutcomes.error = '';

            state.tags.value = [];
            state.tags.error = '';

            state.otherFunding.value = [];
            state.otherFunding.error = '';

            state.geographicAreasServed.value = [];
            state.geographicAreasServed.error = '';

            state.similarCharities.value = [];
            state.similarCharities.error = '';

            resetAiState(state);
        },
        handleEditUserProject: (state, action) => {
            state.openFormDialog = true;
            state.id = action.payload.id;
            state.projectName = action.payload.name; 

            state.name.value = action.payload.name;
            state.name.error = '';

            state.startDate.value = action.payload.startDate;
            state.startDate.error = '';

            state.endDate.value = action.payload.endDate;
            state.endDate.error = '';

            state.projectCost.value = action.payload.projectCost;
            state.projectCost.error = '';

            state.projectDescription.value = action.payload.projectDescription;
            state.projectDescription.error = '';

            state.projectStatus.value = '';
            state.projectStatus.error = '';

            state.targetBeneficiaries.value = action.payload.targetBeneficiaries;
            state.targetBeneficiaries.error = '';

            state.measurableOutcomes.value = action.payload.measurableOutcomes;
            state.measurableOutcomes.error = '';

            state.tags.value = action.payload.tags;
            state.tags.error = '';

            state.otherFunding.value = action.payload.otherFunding;
            state.otherFunding.error = '';

            state.geographicAreasServed.value = (action.payload.geographicAreasServed ?? [])
                .map((area) => {
                    try {
                        return JSON.parse(area);
                    } catch (error) {
                        return null;
                    }
                })
                .filter(Boolean);
            state.geographicAreasServed.error = '';

            state.similarCharities.value = (action.payload.similarCharities ?? [])
                .map((charity) => ({
                    ein: charity.ein,
                    label: charity.name,
                    city: charity.city,
                    state: charity.state,
                    county: charity.county,
                    nteeCode: charity.nteeCode,
                    nteeTitle: charity.nteeTitle,
                }));
            state.similarCharities.error = '';

            resetAiState(state);
            // Editing an existing project: keep the AI-section fields visible only if the project
            // actually has AI-tracked data saved — otherwise AI was never turned on for it, and the
            // toggle should reflect that instead of defaulting to on.
            const hasAiContent = (action.payload.targetBeneficiaries?.length > 0)
                || (action.payload.tags?.length > 0)
                || (action.payload.measurableOutcomes?.length > 0)
                || (action.payload.geographicAreasServed?.length > 0)
                || (action.payload.similarCharities?.length > 0);

            state.aiEnabled = hasAiContent;
            state.aiGenerated = hasAiContent;

            // resetAiState defaults `.edited` to false; restore the persisted status per field so
            // reopening a project doesn't lose track of what was actually manually edited.
            state.targetBeneficiaries.edited = action.payload.targetBeneficiariesEdited ?? false;
            state.tags.edited = action.payload.tagsEdited ?? false;
            state.measurableOutcomes.edited = action.payload.measurableOutcomesEdited ?? false;
            state.geographicAreasServed.edited = action.payload.geographicAreasServedEdited ?? false;
            state.similarCharities.edited = action.payload.similarCharitiesEdited ?? false;
        },
        handleChange: (state, action) => {
            state[action.payload.field].error = action.payload.error ?? "";
            state[action.payload.field].value = action.payload.value;

            if (AI_TRACKED_FIELDS.includes(action.payload.field)) {
                state[action.payload.field].edited = true;
            }
        },
        handleModelErrors: (state, action) => {
            Object.entries(action.payload).forEach(([field, errors]) => { 
                state[field].error = errors[0];
            });
        },
        setTabIndex: (state, action) => {
            state.tabIndex = action.payload;
        },
        setSharedActiveProject: (state, action) => {
            state.pagedSharedList.activeUserProjectId = action.payload;
        },
        handleChangeTags: (state, action) => {
            state.tags.value = action.payload;
            state.tags.error = '';
            state.tags.edited = true;
        },
        // Adds/removes beneficiary-derived keywords in both tags.value and tags.aiSelected together,
        // so keywords that came from selecting/deselecting a target beneficiary are never mistaken
        // for a manual edit (which only ever touches tags.value via handleChangeTags).
        applyBeneficiaryKeywords: (state, action) => {
            const { added = [], removed = [] } = action.payload;

            const nextValue = [...state.tags.value];
            added.forEach((keyword) => {
                if (!nextValue.includes(keyword)) nextValue.push(keyword);
            });
            state.tags.value = removed.length > 0
                ? nextValue.filter((keyword) => !removed.includes(keyword))
                : nextValue;

            const nextAiSelected = [...state.tags.aiSelected];
            added.forEach((keyword) => {
                if (!nextAiSelected.includes(keyword)) nextAiSelected.push(keyword);
            });
            state.tags.aiSelected = removed.length > 0
                ? nextAiSelected.filter((keyword) => !removed.includes(keyword))
                : nextAiSelected;

            state.tags.error = '';
        },
        setAiEnabled: (state, action) => {
            state.aiEnabled = action.payload;

            // Turning AI off must clear the AI-tracked data too, not just hide it — otherwise the
            // stale values get saved anyway and the project reopens with AI back on (its state is
            // re-derived from whether this data is present, see handleEditUserProject above).
            if (!action.payload) {
                state.targetBeneficiaries.value = [];
                state.tags.value = [];
                state.measurableOutcomes.value = [];
                state.geographicAreasServed.value = [];
                state.similarCharities.value = [];

                resetAiState(state);
            }
        },
        setAiFieldGenerating: (state, action) => {
            const { field, generating } = action.payload;
            state[field].generating = generating;
        },
        clearAiFieldJustPopulated: (state, action) => {
            state[action.payload].justPopulated = false;
        },
        setAiFieldConfirmRefresh: (state, action) => {
            const { field, confirmRefresh } = action.payload;
            state[field].confirmRefresh = confirmRefresh;
        },
        applyAiSuggestions: (state, action) => {
            const { targetBeneficiaries, tags, measurableOutcomes } = action.payload;

            if (targetBeneficiaries) {
                state.targetBeneficiaries.list = targetBeneficiaries.options ?? [];
                state.targetBeneficiaries.value = targetBeneficiaries.recommended ?? [];
                state.targetBeneficiaries.aiSelected = targetBeneficiaries.recommended ?? [];
                state.targetBeneficiaries.justPopulated = true;
                state.targetBeneficiaries.edited = false;
            }

            if (tags) {
                state.tags.list = tags.options ?? [];
                state.tags.value = tags.recommended ?? [];
                state.tags.aiSelected = tags.recommended ?? [];
                state.tags.justPopulated = true;
                state.tags.edited = false;
            }

            if (measurableOutcomes) {
                state.measurableOutcomes.list = measurableOutcomes.list ?? [];
                state.measurableOutcomes.justPopulated = true;
            }

            state.aiGenerated = true;
        },
        applyAiFieldSuggestion: (state, action) => {
            const { field, options, recommended, list, mode = "replace" } = action.payload;

            if (field === "measurableOutcomes") {
                const newList = list ?? [];

                if (mode === "merge") {
                    const existingTexts = new Set(state.measurableOutcomes.list.map((item) => item.text));
                    state.measurableOutcomes.list = [
                        ...state.measurableOutcomes.list,
                        ...newList.filter((item) => !existingTexts.has(item.text)),
                    ];
                } else {
                    state.measurableOutcomes.list = newList;
                }

                state.measurableOutcomes.justPopulated = true;
            }
            else {
                state[field].list = options ?? [];

                if (mode === "merge") {
                    const merged = [...state[field].value];
                    (recommended ?? []).forEach((value) => {
                        if (!merged.includes(value)) merged.push(value);
                    });
                    state[field].value = merged;
                } else {
                    state[field].value = recommended ?? [];
                }

                state[field].aiSelected = recommended ?? [];
                state[field].justPopulated = true;
                state[field].edited = (mode === "merge");
            }

            state[field].confirmRefresh = false;
        },
        // Commits the Add Statistical Proof dialog's staged result: value is the final bullet
        // list, aiTexts is whichever of those bullets are still unmodified AI suggestions (used
        // for the Edited badge/Reset) — edited is true unless every bullet is still clean AI content.
        confirmStatisticalProof: (state, action) => {
            const { value, aiTexts } = action.payload;

            state.measurableOutcomes.value = value;
            state.measurableOutcomes.aiTexts = aiTexts;
            state.measurableOutcomes.edited = !(value.length === aiTexts.length && value.every((text) => aiTexts.includes(text)));
            state.measurableOutcomes.error = '';
        },
        resetAiField: (state, action) => {
            const field = action.payload;

            if (field === "measurableOutcomes") {
                state.measurableOutcomes.value = state.measurableOutcomes.aiTexts;
            } else {
                state[field].value = state[field].aiSelected;
            }

            state[field].edited = false;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            userProjectApi.endpoints.getAll.matchFulfilled, (state, action) => {
                state.pagedList.pageNumber = action.payload.pageNumber;
                state.pagedList.pageSize = action.payload.pageSize;
                state.pagedList.pageCount = action.payload.pageCount;
                state.pagedList.totalCount = action.payload.totalCount;
                state.pagedList.orderField = action.payload.orderField;
                state.pagedList.orderDirection = action.payload.orderDirection;
                state.pagedList.items = action.payload.items;
                state.pagedList.activeUserProjectId = action.payload.data.activeUserProjectId;
            }
        ).addMatcher(
            sharedProjectApi.endpoints.getAllShared.matchFulfilled, (state, action) => {
                state.pagedSharedList.pageNumber = action.payload.pageNumber;
                state.pagedSharedList.pageSize = action.payload.pageSize;
                state.pagedSharedList.pageCount = action.payload.pageCount;
                state.pagedSharedList.totalCount = action.payload.totalCount;
                state.pagedSharedList.orderField = action.payload.orderField;
                state.pagedSharedList.orderDirection = action.payload.orderDirection;
                state.pagedSharedList.items = action.payload.items;
                const activeProject = action.payload.items.find(item => item.activeProject === true);
                if(activeProject != null) {
                    state.pagedSharedList.activeUserProjectId = activeProject.id;
                } else {
                    state.pagedSharedList.activeUserProjectId = null;
                }
            }
        )
    }
});

export const {
    handleCloseDialog, 
    handleChange,
    handleModelErrors,
    handleCreateNewProject,
    handleEditUserProject,
    handleOpenDeleteDialog,
    setTabIndex,
    setSharedActiveProject,
    handleChangeTags,
    applyBeneficiaryKeywords,
    setAiEnabled,
    setAiFieldGenerating,
    clearAiFieldJustPopulated,
    setAiFieldConfirmRefresh,
    applyAiSuggestions,
    applyAiFieldSuggestion,
    confirmStatisticalProof,
    resetAiField,
} = userProjectSlice.actions;

export default userProjectSlice.reducer;